import type { MapMode } from '@warplan/shared';

/**
 * The view modes available in the web UI.
 *
 * - `planner` / `operation`  → synchronized with the backend (the live shared
 *   state across all users).
 * - `sandbox-planner` / `sandbox-operation` → fully local-only scratch space
 *   stored in localStorage; backend is never contacted for map data. Sandbox
 *   modes are dissociated by flavour (planner vs operation) so each preserves
 *   its own icon taxonomy and side panel.
 */
export type ViewMode = 'planner' | 'operation' | 'sandbox-planner' | 'sandbox-operation';

export const VIEW_MODES: readonly ViewMode[] = [
  'planner',
  'operation',
  'sandbox-planner',
  'sandbox-operation',
] as const;

export function isViewMode(v: unknown): v is ViewMode {
  return typeof v === 'string' && (VIEW_MODES as readonly string[]).includes(v);
}

/** True when the view mode is one of the sandbox variants. */
export function isSandbox(v: ViewMode): boolean {
  return v === 'sandbox-planner' || v === 'sandbox-operation';
}

/**
 * Returns the underlying `MapMode` (icon taxonomy + side-panel flavour) for
 * a given view mode. Sandbox modes map to their corresponding live flavour.
 */
export function getMapFlavour(v: ViewMode): MapMode {
  if (v === 'operation' || v === 'sandbox-operation') return 'operation';
  return 'planner';
}

/** Returns the sandbox equivalent of a live mode, or the same value if already sandbox. */
export function toSandbox(v: ViewMode): ViewMode {
  if (v === 'sandbox-planner' || v === 'sandbox-operation') return v;
  return v === 'operation' ? 'sandbox-operation' : 'sandbox-planner';
}

/** Returns the live equivalent of a sandbox mode, or the same value if already live. */
export function toLive(v: ViewMode): ViewMode {
  if (v === 'planner' || v === 'operation') return v;
  return v === 'sandbox-operation' ? 'operation' : 'planner';
}
